import type { SupabaseClient } from "@supabase/supabase-js";
import type { Json } from "@/integrations/supabase/types";

export const DEFAULT_ENTRY_METHODS_HEADING = "Earn additional entries";

export type CustomInputType = "text" | "checkbox" | "dropdown" | "date";
export type DateMode = "full" | "month_year";
export type DateRange = "future" | "past" | "both";

export interface FieldValidationRules {
  input_type?: CustomInputType;
  options?: string[];
  date_mode?: DateMode;
  date_range?: DateRange;
  privacy_url?: string;
  terms_url?: string;
}

export interface ContestEntryField {
  id?: string;
  contest_id?: string;
  field_type: "name" | "phone" | "address" | "email_opt_in" | "sms_opt_in" | "custom";
  field_key: string;
  label: string;
  placeholder?: string | null;
  is_required: boolean;
  is_enabled: boolean;
  sort_order: number;
  created_at?: string;
  validation_rules?: FieldValidationRules | null;
}

export const DATE_MONTHS = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
] as const;

export function getCustomInputType(field: Pick<ContestEntryField, "validation_rules">): CustomInputType {
  return field.validation_rules?.input_type || "text";
}

export function getDateMode(field: Pick<ContestEntryField, "validation_rules">): DateMode {
  return field.validation_rules?.date_mode === "month_year" ? "month_year" : "full";
}

export function getDateRange(field: Pick<ContestEntryField, "validation_rules">): DateRange {
  const range = field.validation_rules?.date_range;
  if (range === "future" || range === "past" || range === "both") return range;
  return "both";
}

export function getDropdownOptions(field: Pick<ContestEntryField, "validation_rules">): string[] {
  return (field.validation_rules?.options || []).map((option) => option.trim()).filter(Boolean);
}

export function parseValidationRules(value: unknown): FieldValidationRules {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return value as FieldValidationRules;
}

export function getDateYearOptions(
  range: DateRange = "both",
  pastYears = 120,
  futureYears = 20,
  now = new Date(),
): string[] {
  const current = now.getFullYear();
  if (range === "future") {
    return Array.from({ length: futureYears + 1 }, (_, index) => String(current + index));
  }
  if (range === "past") {
    return Array.from({ length: pastYears + 1 }, (_, index) => String(current - index));
  }
  const future = Array.from({ length: futureYears + 1 }, (_, index) => String(current + index));
  const past = Array.from({ length: pastYears }, (_, index) => String(current - 1 - index));
  return [...future, ...past];
}

export function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

export function parseDateParts(value: string | undefined | null): { year: string; month: string; day: string } {
  if (!value) return { year: "", month: "", day: "" };
  const [year = "", month = "", day = ""] = value.split("-");
  return { year, month, day };
}

export function formatDateValue(year: string, month: string, day: string, mode: DateMode): string {
  if (!year || !month) return "";
  if (mode === "month_year") return `${year}-${month}`;
  if (!day) return "";
  return `${year}-${month}-${day}`;
}

export function isValidDateValue(value: string, mode: DateMode): boolean {
  if (mode === "month_year") {
    return /^\d{4}-(0[1-9]|1[0-2])$/.test(value);
  }
  if (!/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
  );
}

export function isDateInRange(value: string, mode: DateMode, range: DateRange, now = new Date()): boolean {
  if (!isValidDateValue(value, mode)) return false;
  if (range === "both") return true;

  const { year, month, day } = parseDateParts(value);
  if (mode === "month_year") {
    const selected = Number(year) * 12 + Number(month);
    const current = now.getFullYear() * 12 + (now.getMonth() + 1);
    return range === "future" ? selected >= current : selected <= current;
  }

  const selected = Date.UTC(Number(year), Number(month) - 1, Number(day));
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return range === "future" ? selected >= today : selected <= today;
}

export function dateFieldError(
  value: string,
  mode: DateMode,
  range: DateRange,
  required: boolean,
  now = new Date(),
): string | null {
  const trimmed = value.trim();
  if (!trimmed) {
    if (!required) return null;
    return mode === "month_year" ? "Please select a month and year." : "Please select a date.";
  }
  if (!isValidDateValue(trimmed, mode)) {
    return mode === "month_year" ? "Please select a month and year." : "Please select a valid date.";
  }
  if (!isDateInRange(trimmed, mode, range, now)) {
    return range === "past"
      ? "That date is in the future. Choose a past date."
      : "That date has already passed. Choose today or a future date.";
  }
  return null;
}

export function isMonthAllowed(year: string, month: string, range: DateRange, now = new Date()): boolean {
  if (range === "both" || !year) return true;
  const selected = Number(year) * 12 + Number(month);
  const current = now.getFullYear() * 12 + (now.getMonth() + 1);
  if (range === "future") return selected >= current;
  return selected <= current;
}

export function isDayAllowed(year: string, month: string, day: string, range: DateRange, now = new Date()): boolean {
  if (range === "both" || !year || !month) return true;
  const selected = Date.UTC(Number(year), Number(month) - 1, Number(day));
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  if (range === "future") return selected >= today;
  return selected <= today;
}

export function toEntryFieldRows(contestId: string, fields: ContestEntryField[]) {
  return fields.map((field, index) => {
    const rules = { ...(field.validation_rules || {}) };
    if (rules.input_type === "dropdown") {
      rules.options = getDropdownOptions(field);
    }
    return {
      contest_id: contestId,
      field_type: field.field_type,
      field_key: field.field_key,
      label: field.label,
      placeholder: field.placeholder ?? null,
      is_required: !!field.is_required,
      is_enabled: field.is_enabled ?? true,
      sort_order: index,
      validation_rules: rules as Json,
    };
  });
}

export function validateEntryFieldsForSave(fields: ContestEntryField[]): string | null {
  for (const field of fields) {
    if (field.field_type !== "custom") continue;
    if (!field.label?.trim()) {
      return "Each custom field needs a label.";
    }
    if (getCustomInputType(field) === "dropdown" && getDropdownOptions(field).length < 2) {
      return `"${field.label.trim() || "Dropdown"}" needs at least two options.`;
    }
  }
  return null;
}

function isMissingColumnError(error: { message?: string; code?: string } | null, column: string) {
  const message = error?.message || "";
  return message.includes(column) || message.includes("schema cache");
}

async function insertEntryFieldRows(client: SupabaseClient, rows: ReturnType<typeof toEntryFieldRows>) {
  const { error } = await client.from("contest_entry_fields").insert(rows as any);
  if (!error) return;
  if (isMissingColumnError(error, "validation_rules")) {
    const stripped = rows.map(({ validation_rules: _rules, ...rest }) => rest);
    const { error: retryError } = await client.from("contest_entry_fields").insert(stripped as any);
    if (retryError) throw retryError;
    return;
  }
  throw error;
}

export async function persistContestEntryFields(
  client: SupabaseClient,
  contestId: string,
  fields: ContestEntryField[],
) {
  const rows = toEntryFieldRows(contestId, fields);

  if (rows.length > 0) {
    const { error: upsertError } = await client
      .from("contest_entry_fields")
      .upsert(rows as any, { onConflict: "contest_id,field_key" });

    if (upsertError) {
      const canReplaceAll =
        isMissingColumnError(upsertError, "validation_rules") ||
        /no unique|on conflict|conflict/i.test(upsertError.message || "");
      if (!canReplaceAll) throw upsertError;

      const { error: deleteError } = await client
        .from("contest_entry_fields")
        .delete()
        .eq("contest_id", contestId);
      if (deleteError) throw deleteError;
      await insertEntryFieldRows(client, rows);
      return;
    }
  }

  const { data: existing, error: existingError } = await client
    .from("contest_entry_fields")
    .select("field_key")
    .eq("contest_id", contestId);
  if (existingError) throw existingError;

  const keep = new Set(rows.map((row) => row.field_key));
  const keysToDelete = (existing || [])
    .map((row) => row.field_key)
    .filter((key) => !keep.has(key));

  if (keysToDelete.length > 0) {
    const { error: deleteError } = await client
      .from("contest_entry_fields")
      .delete()
      .eq("contest_id", contestId)
      .in("field_key", keysToDelete);
    if (deleteError) throw deleteError;
  }
}

export async function persistEntryMethodsHeading(
  client: SupabaseClient,
  contestId: string,
  showHeading: boolean,
  headingText: string,
) {
  const { error } = await client
    .from("contests")
    .update({
      show_entry_methods_heading: showHeading,
      entry_methods_heading: headingText.trim() || DEFAULT_ENTRY_METHODS_HEADING,
    } as any)
    .eq("id", contestId);

  if (error) throw error;
}
